import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';

import { BaseColumnSelectComponent } from '../base-column-selection.component';
import { ColumnForSelection } from 'src/app/shared/import/column-for-selection';
import { DataService } from 'src/app/shared/network/data.service';
import { ImportProgressService } from 'src/app/shared/import/import-progress.service';
import { ProjectService } from 'src/app/shared/project/project.service';
import { ImportStep } from 'src/app/shared/import/import-step';
import { FormCompVarianceLine, FormulaItemType, ReferenceSettings, RefVolumeBasis } from '../reference/reference-settings';
import { VarianceFormulaBaselineType } from './variance-formula-baseline';

@Component({
  templateUrl: './reference.component.html',
  styleUrls: ['../../general.scss', '../column-selection.scss']
})
export class ReferenceSelectComponent extends BaseColumnSelectComponent implements OnInit {
    FormulaItemType: typeof FormulaItemType = FormulaItemType;
    VarianceFormulaBaselineType: typeof VarianceFormulaBaselineType = VarianceFormulaBaselineType;
    public outcomeName: string;
    public basis: RefVolumeBasis;
    public oldControlValue: string;
    public newControlValue: string;
    public maxPerc: number;
    public oldBaseLine: FormCompVarianceLine[] | null;
    public newBaseLine: FormCompVarianceLine[] | null;
    public canonical: FormCompVarianceLine[] | null;
    public formCompNames: string[] = ['', ''];

    loading = false;
    dataLoaded = false;

    denominatorForm = new FormGroup({
        referenceFrmCtrl: new FormControl(),
    });
    baseLineForm = new FormGroup({
        baseLineFrmCtrl: new FormControl(),
    });

    constructor(private dataService: DataService, private activatedRoute: ActivatedRoute,
        private router: Router, private importProgressService: ImportProgressService, private projectService: ProjectService) {
        super();
    }

    ngOnInit(): void {
        this.activatedRoute.queryParams.subscribe((queryParams: any) => {
            let projectId = queryParams.projectId;
            if (projectId) {
                this.projectId = projectId;
                this.projectService.setProjectId(projectId);
                this.dataService.get<ReferenceSettings>('columnSelection/reference/' + projectId)
                    .then(resp => {
                        this.columns = resp.columns;
                        this.outcomeName = resp.outcomeName;
                        this.oldControlValue = resp.oldControlValue;
                        this.newControlValue = resp.newControlValue;
                        this.maxPerc = resp.maxPerc;
                        this.denominatorForm.controls.referenceFrmCtrl.setValue(resp.basis);
                        this.baseLineForm.controls.baseLineFrmCtrl.setValue(resp.varianceFormulaBaseLine.type);
                        super.selectColumn(resp.columns.filter(c => c.isSelected)[0]);
                        this.oldBaseLine = resp.oldBaseLine;
                        this.newBaseLine = resp.newBaseLine;
                        this.canonical = resp.canonical;
                        this.formCompNames = resp.oldBaseLine.map(x => x.formCompName);
                        this.dataLoaded = true;
                    });
            }
        });
    }

    async save() {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let baseLineType = this.baseLineForm.controls.baseLineFrmCtrl.value;
            await this.dataService.post('columnSelection/reference', {
                projectId: this.projectId,
                columnIndex: this.columns.indexOf(this.selectedColumn),
                basis: this.denominatorForm.controls.referenceFrmCtrl.value,
                maxPerc: this.maxPerc,
                varianceFormulaBaseLine: {
                    type: baseLineType,
                    formCompSeq: baseLineType === VarianceFormulaBaselineType.Canonical 
                                                  ? this.canonical.map(x => x.formCompIx)
                                                  : null,
                },
            });
            this.importProgressService.setImportStep(ImportStep.dimensions);
            this.router.navigateByUrl('/identities?projectId=' + this.projectId);
        } finally {
            this.loading = false;
        }
    }

    public moveDown(i) {
        if (i == this.canonical.length - 1) {
            return;
        }
        let newArray = this.canonical.slice();
        let temp = newArray[i + 1];
        newArray[i + 1] = newArray[i];
        newArray[i] = temp;
        this.reMarkNewAndOld(newArray);
        this.canonical = newArray;
    }

    public moveUp(i: number): void {
        if (i == 0) {
            return;
        }
        let newArray = this.canonical.slice();
        let temp = newArray[i - 1];
        newArray[i - 1] = newArray[i];
        newArray[i] = temp;
        this.reMarkNewAndOld(newArray);
        this.canonical = newArray;
    }

    private isNewOrOldFormComp(formCompSeq: number[], varianceFormCompIx: number, curFormCompIx: number, isNewFormulaPart: boolean): boolean {
        const varFormCompSeqIx = formCompSeq.indexOf(varianceFormCompIx);
        const curFormCompSeqIx = formCompSeq.indexOf(curFormCompIx);
        return isNewFormulaPart
                   ? curFormCompSeqIx <= varFormCompSeqIx
                   : curFormCompSeqIx < varFormCompSeqIx;
    }

    private reMarkNewAndOld(canonical: FormCompVarianceLine[]): void {
        let formCompSeq = canonical.map(x => x.formCompIx);
        for (let line of canonical) {
            for (let item of line.newFormulaPart) {
                if (item.type !== FormulaItemType.Other) {
                    item.type = this.isNewOrOldFormComp(formCompSeq, line.formCompIx, item.formCompIx, true)
                                ? FormulaItemType.NewFormComp
                                : FormulaItemType.OldFormComp;
                }
            }
            for (let item of line.oldFormulaPart) {
                if (item.type !== FormulaItemType.Other) {
                    item.type = this.isNewOrOldFormComp(formCompSeq, line.formCompIx, item.formCompIx, false)
                                ? FormulaItemType.NewFormComp
                                : FormulaItemType.OldFormComp;
                }
            }
        }
    }
}
